// constants.ts
export interface InputValue {
  value: string;
  showError: boolean;
  errorMsg: string | null;
}

export interface InputValues {
  title: InputValue;
  description: InputValue;
  level: InputValue;
  category: InputValue;
  target: { value: string[]; showError: boolean; errorMsg: string | null };
  effectiveDate: InputValue;
  expirationDate: InputValue;
  files: FormData | any;
}

export interface NotificationData {
  title?: string;
  description?: string;
  level?: string;
  category?: string;
  target?: string[];
  effectiveDate?: string;
  expiryDate?: string;
  attachments?: any[];
}

export const DEFAULT_INPUT_VALUES: InputValues = {
  title: { value: "", showError: false, errorMsg: null },
  description: { value: "", showError: false, errorMsg: null },
  level: { value: "", showError: false, errorMsg: null },
  category: { value: "", showError: false, errorMsg: null },
  target: { value: [], showError: false, errorMsg: null },
  effectiveDate: { value: "", showError: false, errorMsg: null },
  expirationDate: { value: "", showError: false, errorMsg: null },
  files: new FormData(),
};

export const ERROR_MESSAGES = {
  TITLE: "Title needs to be at least 10 characters long",
  DESCRIPTION: "Description is required",
  LEVEL: "Level is required",
  CATEGORY: "Category is required",
  TARGET: 'Target is required if category is "TARGET"',
  EFFECTIVE_DATE: "Effective date is required",
  EXPIRATION_DATE: "Expiration date is required",
} as const;

export const STATUS_CONSTANT = {
  draft: "DRAFT",
  waiting: "WAITING_SENT_FOR_PUBLICATION",
  published: "PUBLISHED",
  recall: "RECALL",
};
export const LEVEL_CONSTANT = {
  level1: "LEVEL_1",
  level2: "LEVEL_2",
};

export const SSO_ROLE_CONSTANT = {
  admin: "SSO_ADMIN",
  broadCast: "SSO_BROADCAST_MANAGER",
};
export const NOTIFICATION_MESSAGES = {
  CREATE_SUCCESS: "Notification created successfully",
  UPDATE_SUCCESS: "Notification updated successfully",
  DELETE_SUCCESS: "Notification deleted successfully",
  DRAFT_SUCCESS: "Notification Submitted Successfully",
  ASSIGN_SUCCESS: "User Assigned Successfully",
  PUBLISH_SUCCESS: "Notification Published Successfully",
  REJECT_SUCCESS:
    "Notification Rejected Sucessfully, Notification Moved to Draft State",
  RECALL_SUCCESS:
    "Notfication Recall Successfully, Notification Move To Recall State",
  CREATE_ERROR:
    "An error occurred while creating notification. Please try again later",
  UPDATE_ERROR:
    "An error occurred while updating notification. Please try again later",
  DELETE_ERROR:
    "An error occurred while deleting notification.  Please try again later",
  DRAFT_ERROR:
    "An error occurred while Submit notification.  Please try again later",
  ASSIGN_ERROR:
    "An error occurred while assigning user for notification. Please try again later.",
  PUBLISH_ERROR:
    "An error occurred while Publish notification.  Please try again later",
  REJECT_ERROR:
    "An error occurred while Reject notification.  Please try again later",
  RECALL_ERROR:
    "An error occurred while Recall notification.  Please try again later",
} as const;

export const VALIDATION_RULES = {
  TITLE_MIN_LENGTH: 10,
  TARGET_CATEGORY: "TARGET",
  RESOURCE_TYPE: "COMPANYTYPE",
} as const;

// Helper function to validate inputs
export const validateInput = (
  inputValues: InputValues
): { isValid: boolean; updatedValues: InputValues } => {
  let isValid = true;
  const updatedValues = { ...inputValues };

  // Title validation
  if (
    !inputValues.title.value ||
    (inputValues.title.value as string).length <
      VALIDATION_RULES.TITLE_MIN_LENGTH
  ) {
    updatedValues.title = {
      ...inputValues.title,
      showError: true,
      errorMsg: ERROR_MESSAGES.TITLE,
    };
    isValid = false;
  }

  // Description validation
  if (!inputValues.description.value) {
    updatedValues.description = {
      ...inputValues.description,
      showError: true,
      errorMsg: ERROR_MESSAGES.DESCRIPTION,
    };
    isValid = false;
  }

  // Level validation
  if (!inputValues.level.value) {
    updatedValues.level = {
      ...inputValues.level,
      showError: true,
      errorMsg: ERROR_MESSAGES.LEVEL,
    };
    isValid = false;
  }

  // Category validation
  if (!inputValues.category.value) {
    updatedValues.category = {
      ...inputValues.category,
      showError: true,
      errorMsg: ERROR_MESSAGES.CATEGORY,
    };
    isValid = false;
  }

  // Target validation
  if (
    inputValues.category.value === VALIDATION_RULES.TARGET_CATEGORY &&
    (inputValues.target.value as string[]).length === 0
  ) {
    updatedValues.target = {
      ...inputValues.target,
      showError: true,
      errorMsg: ERROR_MESSAGES.TARGET,
    };
    isValid = false;
  }

  // Effective date validation
  if (!inputValues.effectiveDate.value) {
    updatedValues.effectiveDate = {
      ...inputValues.effectiveDate,
      showError: true,
      errorMsg: ERROR_MESSAGES.EFFECTIVE_DATE,
    };
    isValid = false;
  }

  // Expiration date validation
  if (!inputValues.expirationDate.value) {
    updatedValues.expirationDate = {
      ...inputValues.expirationDate,
      showError: true,
      errorMsg: ERROR_MESSAGES.EXPIRATION_DATE,
    };
    isValid = false;
  }

  return { isValid, updatedValues };
};

// Helper function to prepare notification data
export const prepareNotificationData = (
  inputValues: InputValues,
  userEmail: string
) => {
  return {
    userId: userEmail,
    title: inputValues.title.value,
    description: inputValues.description.value,
    level: inputValues.level.value,
    notificationType: inputValues.category.value,
    target: [
      {
        resourceIDs: inputValues.target.value,
        resourceType: VALIDATION_RULES.RESOURCE_TYPE,
      },
    ],
    effectiveDate: inputValues.effectiveDate.value,
    expiryDate: inputValues.expirationDate.value,
  };
};

//  New helper function to initialize input values from notification data
export const initializeInputValues = (
  notificationData: NotificationData | null
): InputValues => {
  if (!notificationData) {
    return DEFAULT_INPUT_VALUES;
  }

  return {
    title: {
      value: notificationData.title || "",
      showError: false,
      errorMsg: null,
    },
    description: {
      value: notificationData.description || "",
      showError: false,
      errorMsg: null,
    },
    level: {
      value: notificationData.level || "",
      showError: false,
      errorMsg: null,
    },
    category: {
      value: notificationData.category || "",
      showError: false,
      errorMsg: null,
    },
    target: {
      value: Array.isArray(notificationData.target)
        ? notificationData.target
        : [],
      showError: false,
      errorMsg: null,
    },
    effectiveDate: {
      value: notificationData.effectiveDate || "",
      showError: false,
      errorMsg: null,
    },
    expirationDate: {
      value: notificationData.expiryDate || "",
      showError: false,
      errorMsg: null,
    },
    files: Array.isArray(notificationData.attachments)
      ? notificationData.attachments
      : new FormData(),
  };
};
